import React, { useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import axios from "axios";
import dayjs from "dayjs";
import {
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
} from "@heroicons/react/24/solid";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
} from "@mui/material";
import {
  Timeline,
  TimelineItem,
  timelineItemClasses,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from "@mui/lab";
import { toast } from "react-toastify";
import Loader from "../customComponents/customLoader";
import { serviceUrl, genericHeaders } from "../../utils/apiConfig";
import { get_file_from_s3 } from "../../utils";
import { useLazyGetDashBoardReviewsQuery } from "../../api/allReviewsAPI";
import { setDashBoardReviewData } from "../../store/reducers/dashboardReviewsSlice";
import NoContent from "../../Icons/No-Content-Laptop.svg";

const STATUS_STYLES = {
  SUBMITTED: { text: "text-blue-500", dot: "primary" },
  APPROVED:  { text: "text-green-500", dot: "success" },
  REJECTED:  { text: "text-red-500",   dot: "error"   },
};

/* ── Detail modal ─────────────────────────────────────────── */
const StatusReportDetailModal = ({ sheet, onDownload }) => {
  if (!sheet) return null;

  const billingPeriod =
    sheet.billingPeriodMonth && sheet.billingPeriodYear
      ? dayjs()
          .month(sheet.billingPeriodMonth - 1)
          .year(sheet.billingPeriodYear)
          .format("MMMM YYYY")
      : "—";

  const submittedAt = sheet.submittedAt
    ? dayjs(sheet.submittedAt + "Z").format("DD MMM YYYY, hh:mm A")
    : "—";

  const reviewedAt = sheet.reviewedAt
    ? dayjs(sheet.reviewedAt + "Z").format("DD MMM YYYY, hh:mm A")
    : null;

  const statusClass = STATUS_STYLES[sheet.status]?.text ?? "text-gray-500";

  return (
    <div className="flex space-x-8">
      <div className="w-3/4">
        <h2 className="text-black font-semibold mx-2 my-2 text-lg">
          Status Report — {billingPeriod}
        </h2>

        <div className="flex flex-col border border-gray-300 rounded-2xl bg-white">
          <div className="flex justify-between items-center px-6 pt-4 pb-2">
            <div className="flex flex-col">
              <p className="text-sm text-gray-500">
                {sheet.status === "SUBMITTED"
                  ? "Submitted by"
                  : sheet.status === "APPROVED"
                  ? "Approved by"
                  : "Rejected by"}
              </p>
              <p className="text-base font-bold">
                {sheet.status === "SUBMITTED"
                  ? (sheet.submittedByName ?? "—")
                  : (sheet.reviewedByName ?? "—")}
              </p>
            </div>
            <span className={`text-xl font-semibold ${statusClass}`}>
              {sheet.status}
            </span>
          </div>

          <div className="mx-5 mb-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-5 py-3 flex items-center justify-around">
              <div className="flex flex-col items-center">
                <p className="text-xs text-gray-500">Employee</p>
                <p className="font-bold">{sheet.employeeName ?? "—"}</p>
              </div>
              <div className="flex flex-col items-center">
                <p className="text-xs text-gray-500">Month</p>
                <p className="font-bold">
                  {sheet.billingPeriodMonth
                    ? dayjs().month(sheet.billingPeriodMonth - 1).format("MMMM")
                    : "—"}
                </p>
              </div>
              <div className="flex flex-col items-center">
                <p className="text-xs text-gray-500">Year</p>
                <p className="font-bold">{sheet.billingPeriodYear ?? "—"}</p>
              </div>
              <div className="flex flex-col items-center">
                <p className="text-xs text-gray-500">Effective Working Hours</p>
                <p className="font-bold">{sheet.billingHours ?? "—"} hrs</p>
              </div>
            </div>
          </div>

          <div className="border-t mx-5 pt-4 pb-5 space-y-3">
            <h3 className="font-bold text-gray-800">Details</h3>

            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col">
                <p className="text-xs text-gray-500">Uploaded File</p>
                <p className="text-sm font-medium text-gray-700">
                  {sheet.statusReport?.originalFileName ?? "—"}
                </p>
              </div>
              {sheet.statusReport && (
                <button
                  onClick={() => onDownload(sheet.statusReport)}
                  className="text-sm text-blue-500 hover:text-blue-700 underline"
                >
                  Download
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 gap-x-8 gap-y-3 mt-2">
              <div>
                <p className="text-xs text-gray-500">Submitted By</p>
                <p className="text-sm font-medium text-gray-700">
                  {sheet.submittedByName ?? "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Submitted On</p>
                <p className="text-sm font-medium text-gray-700">{submittedAt}</p>
              </div>

              {reviewedAt && (
                <>
                  <div>
                    <p className="text-xs text-gray-500">Reviewed By</p>
                    <p className="text-sm font-medium text-gray-700">
                      {sheet.reviewedByName ?? "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Reviewed On</p>
                    <p className="text-sm font-medium text-gray-700">{reviewedAt}</p>
                  </div>
                </>
              )}

              {sheet.reviewComments && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">Review Comments</p>
                  <p className="text-sm font-medium text-gray-700 italic">
                    "{sheet.reviewComments}"
                  </p>
                </div>
              )}

              {sheet.description && (
                <div className="col-span-2">
                  <p className="text-xs text-gray-500">Description</p>
                  <p className="text-sm text-gray-700">{sheet.description}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="border mt-10 border-gray-300 rounded-2xl bg-white w-1/4 h-full">
        <Timeline
          sx={{
            [`& .${timelineItemClasses.root}:before`]: {
              flex: 0,
              padding: 0,
            },
          }}
          className="my-6"
        >
          {reviewedAt && sheet.reviewedByName && (
            <TimelineItem>
              <TimelineSeparator>
                <TimelineDot color={STATUS_STYLES[sheet.status]?.dot ?? "grey"} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent>
                <p className="text-sm text-gray-700">
                  {sheet.status === "APPROVED" ? "Approved by" : "Rejected by"}
                </p>
                <p className="text-sm font-bold">{sheet.reviewedByName}</p>
                <p className="text-xs text-gray-400">{reviewedAt}</p>
              </TimelineContent>
            </TimelineItem>
          )}

          <TimelineItem>
            <TimelineSeparator>
              <TimelineDot color="primary" />
            </TimelineSeparator>
            <TimelineContent>
              <p className="text-sm text-gray-700">Submitted by</p>
              <p className="text-sm font-bold">{sheet.submittedByName ?? "—"}</p>
              <p className="text-xs text-gray-400">{submittedAt}</p>
            </TimelineContent>
          </TimelineItem>
        </Timeline>
      </div>
    </div>
  );
};

/* ── Main component ─────────────────────────────────────────── */
const ReviewStatusReports = () => {
  const dispatch = useDispatch();
  const statusReports = useSelector(
    (state) => state.dashBoardReview?.statusReports ?? []
  );
  const [getDashBoardReviewData] = useLazyGetDashBoardReviewsQuery();

  const [visibleDetails, setVisibleDetails] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [approveComment, setApproveComment] = useState("");
  const [rejectComment, setRejectComment] = useState("");
  const activeStatusReportId = useRef("");

  const toggleDetails = (id) =>
    setVisibleDetails((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleViewDetails = (sheet) => {
    setSelectedSheet(sheet);
    setDetailModalOpen(true);
  };

  const handleDownload = async (statusReport) => {
    try {
      const s3Url = await get_file_from_s3(statusReport?.fileName);
      if (!s3Url) { toast.error("Failed to fetch the file"); return; }
      const response = await fetch(s3Url);
      const blob = await response.blob();
      const downloadLink = document.createElement("a");
      downloadLink.href = window.URL.createObjectURL(blob);
      downloadLink.setAttribute(
        "download",
        statusReport?.originalFileName || statusReport?.fileName || "status-report"
      );
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    } catch (err) {
      console.error("Error downloading file:", err);
      toast.error("Failed to download file.");
    }
  };

  const refreshData = async () => {
    try {
      const result = await getDashBoardReviewData().unwrap();
      if (result?.data) dispatch(setDashBoardReviewData(result.data));
    } catch (err) {
      console.error(err);
    }
  };

  const openApproveDialog = (id) => {
    activeStatusReportId.current = id;
    setApproveComment("");
    setApproveDialogOpen(true);
  };

  const openRejectDialog = (id) => {
    activeStatusReportId.current = id;
    setRejectComment("");
    setRejectDialogOpen(true);
  };

  const handleApprove = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${serviceUrl}/api/v2/statusReport/approve?statusReportId=${activeStatusReportId.current}`,
        { comments: approveComment },
        { headers: genericHeaders() }
      );
      if (response.status === 200 || response.status === 201) {
        toast.success(response.data?.message || "Status report approved successfully.");
        setApproveDialogOpen(false);
        refreshData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to approve status report.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    setLoading(true);
    try {
      const response = await axios.put(
        `${serviceUrl}/api/v2/statusReport/reject?statusReportId=${activeStatusReportId.current}`,
        { comments: rejectComment },
        { headers: genericHeaders() }
      );
      if (response.status === 200 || response.status === 201) {
        toast.success(response.data?.message || "Status report rejected.");
        setRejectDialogOpen(false);
        refreshData();
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to reject status report.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (statusReports.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center mt-10 pb-6">
        <img src={NoContent} alt="No Content" />
        <p className="text-lg text-gray-500">No status reports pending review.</p>
      </div>
    );
  }

  return (
    <>
      {loading && <Loader />}
      <div className="flex flex-col gap-5">
        {statusReports.map((sheet) => {
          const id = sheet.id ?? sheet._id;
          const isOpen = visibleDetails[id] || false;
          const billingPeriod =
            sheet.billingPeriodMonth && sheet.billingPeriodYear
              ? dayjs()
                  .month(sheet.billingPeriodMonth - 1)
                  .year(sheet.billingPeriodYear)
                  .format("MMMM YYYY")
              : "—";
          const submittedAt = sheet.submittedAt
            ? dayjs(sheet.submittedAt + "Z").format("DD MMM YYYY, hh:mm A")
            : "—";
          const statusClass = STATUS_STYLES[sheet.status]?.text ?? "text-gray-500";

          return (
            <div
              key={id}
              className="w-11/12 my-2 mx-auto border border-slate-300 rounded-xl bg-white"
            >
              <section
                className={`p-3 w-full flex justify-between ${
                  isOpen ? "border-b border-slate-300" : ""
                }`}
              >
                <div className="grid grid-cols-5 gap-8 w-11/12">
                  <div className="my-auto mx-3">
                    <p className="text-xs text-gray-500">Employee</p>
                    <p className="text-base font-medium">{sheet.employeeName ?? "—"}</p>
                  </div>
                  <div className="my-auto mx-3">
                    <p className="text-xs text-gray-500">Month</p>
                    <p className="text-base font-medium">{billingPeriod}</p>
                  </div>
                  <div className="my-auto mx-3">
                    <p className="text-xs text-gray-500">Effective Working Hours</p>
                    <p className="text-base">{sheet.billingHours ?? "—"} hrs</p>
                  </div>
                  <div className="my-auto mx-3 truncate">
                    <p className="text-xs text-gray-500">File</p>
                    <p className="text-sm truncate">
                      {sheet.statusReport?.originalFileName ?? "—"}
                    </p>
                  </div>
                  <div className="my-auto mx-3">
                    <p className={`text-base font-semibold ${statusClass}`}>
                      {sheet.status}
                    </p>
                  </div>
                </div>
                <div className="my-auto w-1/12 flex justify-end pr-3">
                  {isOpen ? (
                    <ChevronDoubleUpIcon
                      className="h-6 text-gray-600 cursor-pointer"
                      onClick={() => toggleDetails(id)}
                    />
                  ) : (
                    <ChevronDoubleDownIcon
                      className="h-6 text-gray-600 cursor-pointer"
                      onClick={() => toggleDetails(id)}
                    />
                  )}
                </div>
              </section>

              <div
                className={`overflow-hidden transition-all ${
                  isOpen ? "max-h-screen" : "max-h-0"
                }`}
              >
                <section className="w-full px-5 py-3 flex flex-col gap-1 border-b border-slate-300">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Submitted by: </span>
                    {sheet.submittedByName ?? "—"}
                  </p>
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Submitted on: </span>
                    {submittedAt}
                  </p>
                </section>

                <section className="w-full flex justify-end gap-3 px-5 py-3">
                  <p
                    className="cursor-pointer text-blue-500 text-sm hover:text-blue-700 my-auto"
                    onClick={() => handleViewDetails(sheet)}
                  >
                    View Details
                  </p>
                  {sheet.statusReport && (
                    <button
                      className="bg-blue-500 hover:bg-white hover:text-blue-500 border border-blue-500 text-white font-semibold py-1 px-3 rounded text-sm"
                      onClick={() => handleDownload(sheet.statusReport)}
                    >
                      Download
                    </button>
                  )}
                  {sheet.status === "SUBMITTED" && (
                    <>
                      <button
                        className="bg-green-500 hover:bg-white hover:text-green-500 border border-green-500 text-white font-semibold py-1 px-3 rounded text-sm"
                        onClick={() => openApproveDialog(id)}
                      >
                        Approve
                      </button>
                      <button
                        className="bg-red-500 hover:bg-white hover:text-red-500 border border-red-500 text-white font-semibold py-1 px-3 rounded text-sm"
                        onClick={() => openRejectDialog(id)}
                      >
                        Reject
                      </button>
                    </>
                  )}
                </section>
              </div>
            </div>
          );
        })}
      </div>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onClose={() => setDetailModalOpen(false)} maxWidth="xl" fullWidth>
        <DialogContent>
          <StatusReportDetailModal sheet={selectedSheet} onDownload={handleDownload} />
        </DialogContent>
        <DialogActions className="my-2 mx-5">
          <Button onClick={() => setDetailModalOpen(false)} color="primary">Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approve Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <p className="text-xl font-semibold text-gray-800 mb-4">
            Are you sure you want to approve this status report?
          </p>
          <div className="w-full flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">Comments</label>
            <textarea
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              className="border border-gray-300 w-full rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
              rows={4}
              placeholder="Enter your comments here (optional)"
            />
          </div>
        </DialogContent>
        <DialogActions className="px-6 pb-4 gap-2">
          <Button onClick={() => setApproveDialogOpen(false)} color="inherit">Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success">Approve</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogContent>
          <p className="text-xl font-semibold text-gray-800 mb-4">
            Are you sure you want to reject this status report?
          </p>
          <div className="w-full flex flex-col gap-2">
            <label className="text-sm font-medium text-gray-700">
              Comments <span className="text-red-500">*</span>
            </label>
            <textarea
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              className="border border-gray-300 w-full rounded-md px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-1 focus:ring-red-400 resize-none"
              rows={4}
              placeholder="Enter your reason for rejection"
            />
          </div>
        </DialogContent>
        <DialogActions className="px-6 pb-4 gap-2">
          <Button onClick={() => setRejectDialogOpen(false)} color="inherit">Cancel</Button>
          <Button
            onClick={handleReject}
            variant="contained"
            color="error"
            disabled={!rejectComment.trim()}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReviewStatusReports;
